import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { ArrowLeft, Mail, Phone, MapPin, Clock, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { usePageContent } from "../hooks/usePageContent";
import { SEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const { data, loading } = usePageContent("contact");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  if (loading) return <div className="px-6 md:px-12 lg:px-24 py-20"><p className="text-sm text-[#9CA3AF]">{t("common.loading")}</p></div>;

  const c = data?.contact || {};

  return (
    <div className="px-6 md:px-12 lg:px-24 py-12 md:py-20 max-w-3xl" data-testid="contact-page">
      <SEO title="Contact Us" description="Contact Mona Luxe for questions about our pre-owned designer handbags. We're here to help with orders, authentication and more." path="/contact" />
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors mb-10">
        <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.back")}
      </Link>
      <h1 className="text-3xl md:text-4xl mb-12" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="contact-title">
        {data?.title || t("footer.contact")}
      </h1>
      {c.intro && <p className="text-sm leading-relaxed tracking-wide text-[#1A1A1A]/80 mb-12">{c.intro}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
        <div className="flex gap-4">
          <Mail size={20} strokeWidth={1.5} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Email</h3>
            {c.email && <p className="text-sm tracking-wide">{c.email}</p>}
            {c.returns_email && <p className="text-sm tracking-wide mt-1">{c.returns_email}</p>}
          </div>
        </div>
        <div className="flex gap-4">
          <Phone size={20} strokeWidth={1.5} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Phone</h3>
            {c.phone && <p className="text-sm tracking-wide">{c.phone}</p>}
            {c.whatsapp && <p className="text-xs text-[#9CA3AF] tracking-wide mt-1">{c.whatsapp}</p>}
          </div>
        </div>
        <div className="flex gap-4">
          <MapPin size={20} strokeWidth={1.5} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Address</h3>
            {(c.address || []).map((line, i) => <p key={i} className="text-sm tracking-wide">{line}</p>)}
          </div>
        </div>
        <div className="flex gap-4">
          <Clock size={20} strokeWidth={1.5} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Business Hours</h3>
            {(c.hours || []).map((line, i) => <p key={i} className="text-sm tracking-wide">{line}</p>)}
          </div>
        </div>
      </div>

      <div className="border-t border-[#E6E2DD] pt-12">
        <h2 className="text-xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{c.form_title || "Send Us a Message"}</h2>
        <form className="space-y-6 max-w-lg" onSubmit={async (e) => {
          e.preventDefault();
          if (!form.name || !form.email || !form.message) { toast.error("Please fill in all fields"); return; }
          setSending(true);
          try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent successfully!");
            setForm({ name: "", email: "", message: "" });
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to send message");
          } finally { setSending(false); }
        }} data-testid="contact-form">
          <input type="text" placeholder="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border-b border-[#E6E2DD] bg-transparent py-3 text-sm tracking-wide outline-none placeholder:text-[#9CA3AF] focus:border-[#1A1A1A] transition-colors" data-testid="contact-name-input" />
          <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border-b border-[#E6E2DD] bg-transparent py-3 text-sm tracking-wide outline-none placeholder:text-[#9CA3AF] focus:border-[#1A1A1A] transition-colors" data-testid="contact-email-input" />
          <textarea placeholder="Your Message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border-b border-[#E6E2DD] bg-transparent py-3 text-sm tracking-wide outline-none resize-none placeholder:text-[#9CA3AF] focus:border-[#1A1A1A] transition-colors" data-testid="contact-message-input" />
          <button type="submit" disabled={sending} className="btn-luxury disabled:opacity-50 inline-flex items-center gap-2" data-testid="contact-submit-btn">
            {sending ? <Loader2 size={14} className="animate-spin" /> : null} Send Message
          </button>
        </form>
      </div>
    </div>
  );
}
